import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'

export interface UseGsapLoadingOptions {
  duration?: number
  delay?: number
  stagger?: number
  animationType?: 'fadeUp' | 'fadeIn' | 'slideLeft' | 'slideRight' | 'scale'
}

/**
 * Custom hook for GSAP loading animations
 * 
 * Usage:
 * ```tsx
 * const containerRef = useGsapLoading({ 
 *   animationType: 'fadeUp', 
 *   duration: 0.6, 
 *   stagger: 0.1 
 * })
 * 
 * return (
 *   <div ref={containerRef}>
 *     <div data-gsap-animate>Item 1</div>
 *     <div data-gsap-animate>Item 2</div>
 *     <div data-gsap-animate>Item 3</div>
 *   </div>
 * )
 * ```
 * 
 * @param options - Animation configuration options
 * @returns React ref to attach to the container element
 */
export function useGsapLoading(options: UseGsapLoadingOptions = {}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const {
    duration = 0.6,
    delay = 0,
    stagger = 0.1,
    animationType = 'fadeUp'
  } = options

  useEffect(() => {
    if (!containerRef.current) return

    // Helper function to animate elements
    const animateElements = (elements: NodeListOf<Element>) => {
      // Set initial state based on animation type
      const getInitialState = () => {
        switch (animationType) {
          case 'fadeUp':
            return { opacity: 0, y: 30 }
          case 'fadeIn':
            return { opacity: 0 }
          case 'slideLeft':
            return { opacity: 0, x: -30 }
          case 'slideRight':
            return { opacity: 0, x: 30 }
          case 'scale':
            return { opacity: 0, scale: 0.8 }
          default:
            return { opacity: 0, y: 30 }
        }
      }

      // Set final state based on animation type
      const getFinalState = () => {
        switch (animationType) {
          case 'fadeUp':
            return { opacity: 1, y: 0 }
          case 'fadeIn':
            return { opacity: 1 }
          case 'slideLeft':
            return { opacity: 1, x: 0 }
          case 'slideRight':
            return { opacity: 1, x: 0 }
          case 'scale':
            return { opacity: 1, scale: 1 }
          default:
            return { opacity: 1, y: 0 }
        }
      }

      // Set initial state immediately
      gsap.set(elements, getInitialState())

      // Animate to final state with stagger
      gsap.to(elements, {
        ...getFinalState(),
        duration,
        delay,
        stagger,
        ease: 'power2.out'
      })
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      if (!containerRef.current) return

      // Find all elements with data-gsap-animate attribute
      const elements = containerRef.current.querySelectorAll('[data-gsap-animate]')
      
      if (elements.length === 0) {
        // If no elements found, try again after a short delay (for async content)
        timeoutId = setTimeout(() => {
          if (!containerRef.current) return
          const retryElements = containerRef.current.querySelectorAll('[data-gsap-animate]')
          if (retryElements.length > 0) {
            animateElements(retryElements)
          }
        }, 100)
        return
      }

      animateElements(elements)
    })

    // Cleanup function
    return () => {
      cancelAnimationFrame(rafId)
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      if (containerRef.current) {
        const elements = containerRef.current.querySelectorAll('[data-gsap-animate]')
        gsap.killTweensOf(elements)
      }
    }
  }, [duration, delay, stagger, animationType])

  return containerRef
}
